import { Link } from "@inertiajs/react";
import React from "react";
import { useScreenSize } from "../../hooks/useScreenSize";

interface ImageCardProps {
  href?: string;
  backgroundImage: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  title?: React.ReactNode;
}

interface ImageCardContentProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
}

interface ImageCardTitleProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
}

interface ImageCardDescriptionProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
}

function ImageCard({ href, backgroundImage, children, style, className, title }: ImageCardProps) {
  const { screenLargerThan } = useScreenSize();
  const isSmall = !screenLargerThan('sm');
  const height = isSmall ? '170px' : '200px'
  const cardContent = (
    <div
      className={`image-card ${className || ''}`}
      style={{
        position: 'relative',
        flex: 1,
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        cursor: href ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        ...style,
      }}
      onMouseEnter={(e) => {
        if (href) {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.2)';
        }
      }}
      onMouseLeave={(e) => {
        if (href) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
        }
      }}
    >
      {/* Title section with blurred background */}
      {title && (
        <div
          className="image-card-header"
          style={{
            position: 'relative',
            width: '100%',
            overflow: 'hidden',
          }}
        >
          {/* Blurred background image */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'blur(12px)',
              transform: 'scale(1.1)',
            }}
          />
          {/* Dark overlay for readability */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
            }}
          />
          {/* Title content */}
          <div
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              zIndex: 1,
            }}
          >
            <div
              style={{
                width: '4px',
                height: '1.2em',
                backgroundColor: 'var(--color-primary)',
                borderRadius: '2px',
                flexShrink: 0,
              }}
            />
            <h2
              className="text-2xl"
              style={{
                margin: 0,
                fontWeight: 'bold',
                color: 'white',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
              }}
            >
              {title}
            </h2>
          </div>
        </div>
      )}

      {/* Image section */}
      <div style={{ position: 'relative', height }}>
        {/* Top half - clear background */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '50%',
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />

        {/* Bottom half - blurred background */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(24px)',
            transform: 'scale(1.1)', // Prevent blur edge artifacts
          }}
        />

        {/* Dark overlay for bottom half to improve text readability */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
          }}
        />

        {/* Content container */}
        <div
          style={{
            position: 'relative',
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );

  if (href) {
    return (
      <Link href={href} style={{ textDecoration: 'none', display: 'block' }}>
        {cardContent}
      </Link>
    );
  }

  return cardContent;
}

function ImageCardContent({ children, style }: ImageCardContentProps) {
  return (
    <>
      <div
        className="image-card-content"
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '50%',
          padding: '10px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '8px',
          color: 'white',
          ...style,
        }}
      >
        {children}
      </div>
      <style>{`
        .image-card-content {
          padding: 20px;
        }
        @media (max-width: 640px) {
          .image-card-content {
            padding: 12px;
            gap: 4px;
          }
        }
      `}</style>
    </>
  );
}

function ImageCardTitle({ children, style }: ImageCardTitleProps) {
  return (
    <>
      <div className="image-card-title-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div
          style={{
            width: '4px',
            height: '1.2em',
            backgroundColor: 'var(--color-primary)',
            borderRadius: '2px',
            flexShrink: 0,
          }}
        />
        <h2
          className="image-card-title"
          style={{
            margin: 0,
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: 'white',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
            ...style,
          }}
        >
          {children}
        </h2>
      </div>
      <style>{`
        .image-card-title {
          font-size: 1.5rem;
        }
        @media (max-width: 640px) {
          .image-card-title {
            font-size: 1.1rem;
          }
        }
      `}</style>
    </>
  );
}

function ImageCardDescription({ children, style }: ImageCardDescriptionProps) {
  return (
    <>
      <div
        className="image-card-description"
        style={{
          margin: 0,
          fontSize: '0.9rem',
          color: 'rgba(255, 255, 255, 0.9)',
          textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
          lineHeight: 1.4,
          height: '100%',
          ...style,
        }}
      >
        {children}
      </div>
      <style>{`
        .image-card-description {
          font-size: 0.9rem;
        }
        @media (max-width: 640px) {
          .image-card-description {
            font-size: 0.8rem;
            line-height: 1.3;
          }
        }
      `}</style>
    </>
  );
}

// Attach sub-components to main component
ImageCard.Content = ImageCardContent;
ImageCard.Title = ImageCardTitle;
ImageCard.Description = ImageCardDescription;

export default ImageCard;
